import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { SERVICES, BRAND } from "@/data/services";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [params] = useSearchParams();
  const preselect = params.get("service") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    is_free_trial: false,
  });
  const [selected, setSelected] = useState(preselect ? [preselect] : []);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (preselect && !selected.includes(preselect)) {
      setSelected((s) => [...s, preselect]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselect]);

  const toggleSvc = (slug) => {
    setSelected((s) =>
      s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug],
    );
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        interested_services: selected,
      });
      toast.success("Thanks! We'll be in touch within 1 business day.");
      setSuccess(true);
      setForm({
        name: "",
        email: "",
        company: "",
        message: "",
        is_free_trial: false,
      });
      setSelected([]);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-zinc-50 border-b border-zinc-200 overflow-hidden ruler-top">
        <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              Contact · Get started
            </div>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium text-zinc-950 leading-[0.95]">
              Tell us about your project.
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
              Share a few details and your interested services. We respond within 1 business day —
              often same day.
            </p>
          </div>
          <div className="lg:col-span-4 border border-zinc-900 bg-white p-6 font-mono text-xs">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 pb-3 border-b border-dashed border-zinc-300">
              Response Window
            </div>
            <div className="flex justify-between py-1"><span className="text-zinc-500">Reply</span><span className="text-zinc-900">&lt; 24 hrs</span></div>
            <div className="flex justify-between py-1"><span className="text-zinc-500">Quote</span><span className="text-zinc-900">&lt; 48 hrs</span></div>
            <div className="flex justify-between py-1"><span className="text-zinc-500">First draft</span><span className="text-zinc-900">3–5 days</span></div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            {success ? (
              <div
                className="border border-zinc-900 p-10 bg-zinc-50"
                data-testid="contact-success"
              >
                <CheckCircle size={36} weight="duotone" className="text-[#0044FF]" />
                <h2 className="mt-6 font-display text-3xl tracking-tight text-zinc-950">
                  Thanks — we got it.
                </h2>
                <p className="mt-3 text-zinc-600 max-w-lg">
                  A member of our team will reach out within 1 business day. For urgent
                  projects, request a free trial and upload your data directly.
                </p>
                <div className="mt-8 flex gap-3">
                  <button
                    onClick={() => setSuccess(false)}
                    className="px-5 py-3 border border-zinc-900 text-zinc-950 font-medium btn-lift hover:bg-zinc-900 hover:text-white"
                    data-testid="contact-send-another"
                  >
                    Send another
                  </button>
                  <Link
                    to="/free-trial"
                    className="px-5 py-3 bg-[#0044FF] text-white font-medium btn-lift inline-flex items-center gap-2"
                  >
                    Start free trial <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="border border-zinc-300 bg-white p-6 md:p-10"
                data-testid="contact-form"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="contact-name" />
                  <Field label="Work email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
                  <div className="md:col-span-2">
                    <Field label="Company" name="company" value={form.company} onChange={onChange} testId="contact-company" />
                  </div>
                </div>

                <div className="mt-8">
                  <label className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-3">
                    Interested services (multi-select)
                  </label>
                  <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-300"
                    data-testid="contact-services-grid"
                  >
                    {SERVICES.map((s) => {
                      const on = selected.includes(s.slug);
                      return (
                        <button
                          type="button"
                          key={s.slug}
                          onClick={() => toggleSvc(s.slug)}
                          className={`text-left p-4 text-sm transition-colors ${
                            on
                              ? "bg-[#0044FF] text-white"
                              : "bg-white text-zinc-800 hover:bg-zinc-50"
                          }`}
                          data-testid={`svc-opt-${s.slug}`}
                          aria-pressed={on}
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className={`mt-1 w-3 h-3 border ${on ? "bg-white border-white" : "border-zinc-400"}`}
                              aria-hidden="true"
                            />
                            <span>{s.title}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8">
                  <label
                    htmlFor="message"
                    className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-2"
                  >
                    Project details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Scope, turnaround, jurisdiction, anything we should know…"
                    className="w-full border border-zinc-300 bg-white p-4 text-sm focus:outline-none focus:border-[#0044FF] focus:ring-2 focus:ring-[#0044FF]/20"
                    data-testid="contact-message"
                  />
                </div>

                <label className="mt-6 flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_free_trial"
                    checked={form.is_free_trial}
                    onChange={onChange}
                    className="mt-1 w-4 h-4 accent-[#0044FF]"
                    data-testid="contact-is-free-trial"
                  />
                  <span className="text-zinc-700">
                    I&apos;d also like to start a <strong className="text-zinc-950">free trial</strong> and
                    upload sample data.{" "}
                    <span className="text-zinc-500">(You can upload files on the Free Trial page.)</span>
                  </span>
                </label>

                <div className="mt-10 flex flex-wrap gap-3 items-center justify-between">
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-[0.2em]">
                    We respond within 1 business day.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-4 bg-[#0044FF] hover:bg-[#0033CC] text-white font-medium btn-lift inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <div className="border border-zinc-200 bg-zinc-50 p-8">
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-4">
                Prefer to skip the form?
              </div>
              <h3 className="font-display text-2xl tracking-tight text-zinc-950">
                Start a free trial instead.
              </h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                Upload a point file or field notes directly. We&apos;ll draft a sample deliverable
                on us.
              </p>
              <Link
                to="/free-trial"
                className="mt-5 inline-flex items-center gap-2 px-4 py-3 bg-zinc-950 text-white text-sm font-medium btn-lift"
                data-testid="contact-free-trial-link"
              >
                Go to free trial <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="mt-6 border border-zinc-200 bg-white p-8 font-mono text-xs space-y-2">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 pb-2 border-b border-dashed border-zinc-200">
                Reach us directly
              </div>
              <Link
                to="/contact"
                className="block text-zinc-900 hover:text-[#0044FF] break-all"
                data-testid="contact-email-gmail"
              >
                {BRAND.email}
              </Link>
              <a
                href={BRAND.tel_url}
                className="block text-zinc-900 hover:text-[#0044FF]"
                data-testid="contact-phone"
              >
                {BRAND.phone_display}
              </a>
              <a
                href={BRAND.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-900 hover:text-[#25D366] inline-flex items-center gap-1.5"
                data-testid="contact-whatsapp"
              >
                <span className="inline-block w-2 h-2 bg-[#25D366] rounded-full" aria-hidden="true" />
                WhatsApp {BRAND.phone_display}
              </a>
              <div className="pt-3 mt-2 border-t border-dashed border-zinc-200 flex justify-between">
                <span className="text-zinc-500">Hours</span>
                <span className="text-zinc-900">Mon–Sat, EST</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Coverage</span>
                <span className="text-zinc-900">USA · 50 states</span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-2"
      >
        {label} {required && <span className="text-[#FF5500]">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full border border-zinc-300 bg-white p-3 text-sm focus:outline-none focus:border-[#0044FF] focus:ring-2 focus:ring-[#0044FF]/20"
        data-testid={testId}
      />
    </div>
  );
}
